import { mediaUrl } from "../lib/media";

/**
 * The image slot on a project card.
 *
 * **To supply real images:** drop files in `frontend/public/projects/` and add
 * them to `PROJECT_IMAGES` below, keyed by slug. Anything not listed keeps the
 * generated fallback, so images can arrive one at a time rather than all or
 * nothing.
 *
 * The fallback is generated from the slug rather than being a grey rectangle.
 * A hash seeds a small node graph, so every project gets a visual that is
 * distinct from its neighbours and *identical on every render* — a random one
 * would change on each navigation, which reads as a glitch. It also echoes the
 * hero's network instead of introducing a second unrelated idea, and it costs
 * no network request.
 *
 * Always `aria-hidden`. It carries no information the title does not, and a
 * real screenshot would still be decorative next to a written case study.
 */

/**
 * Keyed by project slug. Files live in `public/projects/`.
 *
 * WebP at 1400px wide. The originals were 2816px PNGs averaging 7.5 MB each —
 * 74 MB across ten cards, for images rendered about 530px wide. That is not a
 * slow page, it is an unusable one on the mobile connections most of this
 * site's traffic arrives on, and no amount of bundle-splitting elsewhere
 * matters next to it.
 */
const PROJECT_IMAGES: Record<string, string> = {
  "agentguard-api": "/projects/agentguard-api.webp",
  "ai-lead-triage": "/projects/ai-lead-triage.webp",
  "ap-invoice-agent": "/projects/ap-invoice-agent.webp",
  "awlar-ai": "/projects/awlar-ai.webp",
  "caiet-website": "/projects/caiet-website.webp",
  "cifar10-transfer-learning": "/projects/cifar10-transfer-learning.webp",
  "crop-yield-mlp": "/projects/crop-yield-mlp.webp",
  "giki-rag-chatbot": "/projects/giki-rag-chatbot.webp",
  "lamha": "/projects/lamha.webp",
  "mathaccess-ai": "/projects/mathaccess-ai.webp",
  "one-health-international": "/projects/one-health-international.webp",
  "support-rag-assistant": "/projects/support-rag-assistant.webp",
  "triageai": "/projects/triageai.webp",
  "voice-clone-studio": "/projects/voice-clone-studio.webp",
  "yolov8-campus-detection": "/projects/yolov8-campus-detection.webp",
  "youtube-sentiment-analysis": "/projects/youtube-sentiment-analysis.webp",
};

/**
 * FNV-1a. Small, fast, and stable across runs — which is the whole point:
 * `Math.random()` here would redraw the artwork on every re-render.
 */
function hash(slug: string): number {
  let h = 2166136261;
  for (let i = 0; i < slug.length; i += 1) {
    h ^= slug.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Mulberry32 — a seeded PRNG, so the same slug always draws the same graph. */
function seeded(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Node {
  x: number;
  y: number;
  r: number;
}

function buildGraph(slug: string): { nodes: Node[]; links: [number, number][] } {
  const random = seeded(hash(slug));
  const count = 9 + Math.floor(random() * 4);
  const nodes: Node[] = [];

  for (let i = 0; i < count; i += 1) {
    nodes.push({
      // Inset from the edges so nothing is clipped by the card's radius.
      x: 12 + random() * 76,
      y: 16 + random() * 68,
      r: 1.2 + random() * 2.6,
    });
  }

  // Each node links to its nearest neighbour ahead of it in the list. That
  // guarantees a connected-looking figure without an O(n²) pass or the risk
  // of a lone dot floating unattached.
  const links: [number, number][] = [];
  for (let i = 0; i < nodes.length - 1; i += 1) {
    let best = i + 1;
    let bestDistance = Infinity;
    for (let j = i + 1; j < nodes.length; j += 1) {
      const d = (nodes[i].x - nodes[j].x) ** 2 + (nodes[i].y - nodes[j].y) ** 2;
      if (d < bestDistance) {
        bestDistance = d;
        best = j;
      }
    }
    links.push([i, best]);
  }
  return { nodes, links };
}

export function ProjectVisual({
  slug,
  coverUrl,
  className = "",
}: {
  slug: string;
  /** An uploaded cover from the admin. Wins over the bundled file. */
  coverUrl?: string | null;
  className?: string;
}) {
  // Uploaded first, then the bundled file, then the generated graph. The order
  // is what makes the admin's image picker mean anything: before it, changing a
  // project's picture meant editing PROJECT_IMAGES and redeploying the site.
  const image = coverUrl ? mediaUrl(coverUrl) : PROJECT_IMAGES[slug];

  if (image) {
    return (
      <img
        src={image}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className={`size-full object-cover ${className}`}
      />
    );
  }

  const { nodes, links } = buildGraph(slug);

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      className={`size-full ${className}`}
    >
      <rect
        width="100"
        height="100"
        fill="color-mix(in oklab, var(--color-accent) 7%, transparent)"
      />
      <g stroke="var(--color-accent)" strokeWidth="0.4" opacity="0.5">
        {links.map(([a, b]) => (
          <line
            key={`${slug}-${String(a)}-${String(b)}`}
            x1={nodes[a].x}
            y1={nodes[a].y}
            x2={nodes[b].x}
            y2={nodes[b].y}
          />
        ))}
      </g>
      <g fill="var(--color-accent)">
        {nodes.map((node, index) => (
          <circle
            key={`${slug}-n${String(index)}`}
            cx={node.x}
            cy={node.y}
            r={node.r}
            opacity={0.35 + (index % 3) * 0.22}
          />
        ))}
      </g>
    </svg>
  );
}
